import React, { useState, useEffect } from "react";
import { View, ScrollView, Platform } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { AppText } from "@/components/ui/AppText";
import { Badge } from "@/components/ui/Badge";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { PageFooter } from "@/components/ui/PageFooter";
import { useI18n } from "@/context/I18nContext";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { vendorStageLabels } from "@/constants";
import { useQuery } from "@tanstack/react-query";
import { vendorApi, type VendorApplicationData } from "@/lib/api/vendor";
import { useApplicationDetail, APPLICATION_DETAIL_QUERY_KEY } from "./useApplicationDetail";
import { VENDOR_APPLICATION_QUERY_KEY } from "../VendorApplicationScreen";
import type { ApplicationStageId, ApplicationStageStatus } from "@/lib/api/vendor";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { useAuth } from "@/context/AuthContext";
import { LegalRepresentativeContent } from "./components/LegalRepresentativeContent";
import { BusinessInformationContent } from "./components/BusinessInformationContent";
import { ProductComplianceContent } from "./components/ProductComplianceContent";
import {
  FinancialSettlementContent,
  FinancialSettlementTabBar,
  type FinancialTab,
} from "./components/FinancialSettlementContent";
import { BrandingLocalizationContent } from "./components/BrandingLocalizationContent";
import { cn } from "@/lib/utils";

// ─── Main screen ─────────────────────────────────────────────────────────────

export default function VendorApplicationStageScreen() {
  const { t } = useI18n();
  const isWide = useIsWideScreen();
  const router = useRouter();
  const {
    stage,
    tab: tabParam,
    updatedField,
  } = useLocalSearchParams<{
    stage: string;
    tab?: FinancialTab;
    updatedField?: string;
  }>();

  const stageId = stage as ApplicationStageId;

  const [locallyUpdatedFields, setLocallyUpdatedFields] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (updatedField) {
      setLocallyUpdatedFields((prev) => new Set([...prev, updatedField]));
    }
  }, [updatedField]);

  const handleMakeUpdate = (fieldKey: string) => {
    router.push({
      pathname: "/(vendor)/application/[stage]/update",
      params: { stage: stageId, fieldKey },
    });
  };

  const { data: applicationDetail } = useApplicationDetail();
  const { user } = useAuth();
  const { locale } = useI18n();
  const applicationId = user?.applicationId ?? null;
  const { data: applicationData } = useQuery<VendorApplicationData>({
    queryKey: [...VENDOR_APPLICATION_QUERY_KEY, applicationId, locale],
    queryFn: () => vendorApi.getApplicationData(applicationId!, locale),
    enabled: !!applicationId,
  });

  const stageData = applicationData?.stages.find((s) => s.id === stageId);
  const status: ApplicationStageStatus = stageData?.status ?? "not_started";

  const badgeVariant =
    status === "completed"
      ? "filled-success"
      : status === "in_progress" || status === "update_required"
        ? "filled-warning"
        : "default";

  const statusLabelKey: TranslationKeys =
    status === "completed"
      ? "vendor.application.stages.completed"
      : status === "in_progress"
        ? "vendor.application.stages.inProgress"
        : status === "update_required"
          ? "vendor.application.stages.updateRequired"
          : "vendor.application.stages.notStarted";

  const stageLabel = vendorStageLabels[stageId];

  const appStatus = applicationDetail?.status;
  const isUpdateRequired = status === "update_required";
  const isAllApproved = status === "completed" && appStatus === "approved";
  const isAllReviewed = status === "completed" && appStatus === "rejected";
  const isUnderReview = appStatus === "pending_review" && !isUpdateRequired;
  const rejectedFieldCount = Object.keys(applicationDetail?.fieldRejections ?? {}).length;

  const financialTab: FinancialTab =
    tabParam === "alipay" || tabParam === "wechatPay" ? tabParam : "bank";

  const handleFinancialTabChange = (tab: FinancialTab) => {
    router.setParams({ tab });
  };

  return (
    <View className="flex-1 bg-stroke-10">
      <View
        className={cn(
          "px-page-x bg-white py-4 border-b border-stroke-50",
          Platform.select({ web: "hidden tablet:flex", native: "hidden" })
        )}
      >
        <Breadcrumb
          segments={[
            {
              labelKey: "vendor.dashboard.nav.application",
              href: "/(vendor)/(dashboard)/application",
            },
            { labelKey: stageLabel?.title ?? "vendor.dashboard.nav.application" },
          ]}
        />
      </View>
      <ScrollView
        className={`flex-1 ${isWide ? "bg-stroke-20" : "bg-white"}`}
        contentContainerStyle={{ flexGrow: 1 }}
        testID={`vendor-application-detail-${stageId}`}
      >
        {/* ── Desktop: breadcrumb + title band ──────────────────────── */}
        {isWide && Platform.OS === "web" && (
          <View className="bg-white px-20 pt-5 pb-6 items-start">
            <View className="flex-row items-center gap-3 mt-4">
              <AppText
                weight="bold"
                className="text-[29px] leading-[32px] tracking-[-0.29px] text-dark-text-50"
              >
                {t(stageLabel?.title ?? "vendor.dashboard.nav.application")}
              </AppText>
              <Badge variant={badgeVariant} shape="rounded" label={statusLabelKey} />
            </View>
            {stageLabel?.subtitle && (
              <AppText weight="medium" className="text-base text-supporting-text-40 mt-1">
                {t(stageLabel.subtitle)}
              </AppText>
            )}
          </View>
        )}
        {/* Financial Settlement tab bar — rendered above approval banner */}
        {stageId === "financial_settlement" && (
          <FinancialSettlementTabBar
            activeTab={financialTab}
            onTabPress={handleFinancialTabChange}
            isWide={isWide}
          />
        )}

        {/* ── Content ─────────────────────────────────────────────────── */}
        <View className={`gap-4 max-w-8xl self-center w-full ${isWide ? "px-10 py-6" : "py-6"}`}>
          {/* Approval status banner */}
          {isAllApproved && (
            <Badge
              variant="accent-green"
              label="vendor.application.detail.allFieldsApproved"
              className={cn("py-2 px-3", isWide ? "self-start" : "mx-5")}
            />
          )}
          {isAllReviewed && (
            <Badge
              variant="neutral"
              label="vendor.application.detail.allFieldsReviewed"
              className={cn("py-2 px-3", isWide ? "self-start" : "mx-5")}
            />
          )}
          {isUnderReview && (
            <Badge
              variant="neutral"
              label="vendor.application.detail.allFieldsUnderReview"
              className={cn("py-2 px-3", isWide ? "self-start" : "mx-5")}
            />
          )}
          {isUpdateRequired && rejectedFieldCount > 0 && (
            <Badge
              variant="accent-orange"
              label={`${rejectedFieldCount} ${t("vendor.application.detail.fieldsFlaggedForUpdate")}`}
              className={cn("py-2 px-3", isWide ? "self-start" : "mx-5")}
            />
          )}

          {/* Fields card */}
          <View
            testID="detail-fields-card"
            className={`bg-white overflow-hidden ${
              isWide ? "rounded-lg border border-stroke-50 py-6" : ""
            }`}
          >
            {stageId === "legal_representative" && (
              <LegalRepresentativeContent
                isWide={isWide}
                stageStatus={status}
                onMakeUpdate={handleMakeUpdate}
                updatedFields={locallyUpdatedFields}
                application={applicationDetail}
              />
            )}
            {stageId === "business_information" && (
              <BusinessInformationContent
                isWide={isWide}
                stageStatus={status}
                onMakeUpdate={handleMakeUpdate}
                updatedFields={locallyUpdatedFields}
                application={applicationDetail}
              />
            )}
            {stageId === "product_compliance" && (
              <ProductComplianceContent
                isWide={isWide}
                stageStatus={status}
                onMakeUpdate={handleMakeUpdate}
                updatedFields={locallyUpdatedFields}
                application={applicationDetail}
              />
            )}
            {stageId === "financial_settlement" && (
              <FinancialSettlementContent
                isWide={isWide}
                activeTab={financialTab}
                stageStatus={status}
                onMakeUpdate={handleMakeUpdate}
                updatedFields={locallyUpdatedFields}
                application={applicationDetail}
              />
            )}
            {stageId === "branding_localization" && (
              <BrandingLocalizationContent
                isWide={isWide}
                stageStatus={status}
                onMakeUpdate={handleMakeUpdate}
                updatedFields={locallyUpdatedFields}
                application={applicationDetail}
              />
            )}
          </View>
        </View>

        <PageFooter />
      </ScrollView>
    </View>
  );
}
